import React, { PropsWithChildren, createContext, useContext } from 'react'
import { QueryInputType, QueryInputValues, useCallQueryInput } from './query-input'
import {
    ClinicFieldsFragment,
    CallFieldsFragment,
    useQueryClinicCallsQuery,
    useCountCallsQuery,
} from '~graphql/generated/graphql'
import { eqStringFilter } from '~utils/query-helpers'

interface Props {
    clinic: ClinicFieldsFragment
}

export function useCallsContext() {
    return useContext(CallsContext)
}

export default function CallsProvider({
    clinic,
    children,
}: PropsWithChildren<Props>) {
    const {
        queryInput,
        orderBy,
        setOrderBy,
        debouncedOrderBy,
        page,
        pageSize,
        ...queryInputValues
    } = useCallQueryInput()

    const { data, loading } = useQueryClinicCallsQuery({
        variables: {
            clinicId: clinic.id,
            where: queryInput,
            page,
            pageSize,
            orderBy,
        },
    })

    const { data: countData } = useCountCallsQuery({
        variables: {
            where: [eqStringFilter('clinic', clinic.id), ...queryInput],
        },
    })

    const calls = data?.queryClinicCalls || []
    const count = countData?.countCalls || 0

    return (
        <CallsContext.Provider
            value={{
                clinic,
                calls,
                count,
                loading,

                orderBy,
                setOrderBy,
                debouncedOrderBy,
                queryInput,
                page,
                pageSize,
                ...queryInputValues,
            }}
        >
            {children}
        </CallsContext.Provider>
    )
}

type CallsContextType = {
    clinic: ClinicFieldsFragment
    calls: CallFieldsFragment[]
    count: number
    loading: boolean
} & QueryInputType &
    QueryInputValues

const CallsContext = createContext<CallsContextType>({} as CallsContextType)
